/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import {
  GitBranch,
  Route,
  Cloud,
  Network,
  Globe,
  Link2,
  Cpu,
  CheckCircle2,
  AlertCircle,
  XCircle,
} from 'lucide-react';
import type {
  TGWNodeData,
  TGWRouteTableNodeData,
  TGWAttachmentNodeData,
} from './types';
import { ATTACHMENT_STYLES, formatAsn } from './types';

// =============================================================================
// TGW MAIN NODE
// =============================================================================

export const TGWNode: React.FC<NodeProps<TGWNodeData>> = ({ data }) => {
  const stateColor = data.state === 'available' 
    ? 'bg-emerald-100 text-emerald-700' 
    : 'bg-amber-100 text-amber-700';

  return (
    <div className="tgw-node bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-500 rounded-xl p-4 min-w-[320px] shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
          <GitBranch className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-purple-900 truncate">{data.name}</h3>
          <p className="text-xs text-purple-600 font-mono truncate">{data.id}</p>
        </div>
        <span className={`px-2 py-1 text-xs rounded-full font-medium ${stateColor}`}>
          {data.state}
        </span>
      </div>
      
      {/* Metadata Grid */}
      <div className="grid grid-cols-3 gap-2 text-xs bg-white/50 rounded-lg p-2">
        <div className="text-center">
          <div className="text-purple-500 font-medium">ASN</div>
          <div className="text-purple-800 font-mono">{formatAsn(data.asn)}</div>
        </div>
        <div className="text-center border-x border-purple-200">
          <div className="text-purple-500 font-medium">Region</div>
          <div className="text-purple-800 truncate">{data.region || 'N/A'}</div>
        </div>
        <div className="text-center">
          <div className="text-purple-500 font-medium">Owner</div>
          <div className="text-purple-800">{data.isOwner ? 'Yes' : 'No'}</div>
        </div>
      </div>

      {/* Summary */}
      <div className="flex justify-between mt-3 text-xs">
        <div className="flex items-center gap-1 text-purple-700">
          <Route className="w-3.5 h-3.5" />
          <span>{data.routeTableCount} Route Tables</span>
        </div>
        <div className="flex items-center gap-1 text-purple-700">
          <Link2 className="w-3.5 h-3.5" />
          <span>{data.attachmentCount} Attachments</span>
        </div>
      </div>
      
      {/* Handles */}
      <Handle type="source" position={Position.Bottom} className="!bg-purple-500" />
      <Handle type="target" position={Position.Top} className="!bg-purple-500" />
    </div>
  );
};

// =============================================================================
// TGW ROUTE TABLE NODE
// =============================================================================

export const TGWRouteTableNode: React.FC<NodeProps<TGWRouteTableNodeData>> = ({ data }) => {
  const bgColor = data.isDefault ? 'bg-purple-100' : 'bg-purple-50';
  const borderColor = data.isDefault ? 'border-purple-500' : 'border-purple-300';

  // Count routes by type
  const staticCount = data.routes.filter(r => r.type === 'static').length;
  const propagatedCount = data.routes.filter(r => r.type === 'propagated').length;
  const activeCount = data.routes.filter(r => r.state === 'active').length;

  return (
    <div className={`tgw-rtb-node rounded-lg p-3 ${bgColor} ${borderColor} border-2 min-w-[180px] shadow-md`}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Route className="w-5 h-5 text-purple-600" />
        <span className="font-medium text-purple-900 truncate flex-1 text-sm">{data.name}</span>
        {data.isDefault && (
          <span className="px-1.5 py-0.5 text-[10px] bg-purple-500 text-white rounded font-medium">
            DEFAULT
          </span>
        )}
      </div>
      
      {/* Route Stats */}
      <div className="bg-white/60 rounded p-2 text-xs space-y-1">
        <div className="flex justify-between">
          <span className="text-purple-600">Total Routes:</span>
          <span className="font-mono text-purple-900">{data.routeCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-purple-600">Static:</span>
          <span className="font-mono text-blue-700">{staticCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-purple-600">Propagated:</span>
          <span className="font-mono text-green-700">{propagatedCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-purple-600">Active:</span>
          <span className="font-mono text-emerald-700">{activeCount}</span>
        </div>
      </div>

      {/* ID */}
      <div className="mt-2 text-[10px] font-mono text-purple-500 truncate">
        {data.id}
      </div>
      
      <Handle type="target" position={Position.Top} className="!bg-purple-400" />
      <Handle type="source" position={Position.Bottom} className="!bg-purple-400" />
    </div>
  );
};

// =============================================================================
// TGW ATTACHMENT NODE
// =============================================================================

const AttachmentIcon: React.FC<{ type: string; className?: string }> = ({ type, className = 'w-5 h-5' }) => {
  switch (type) {
    case 'vpc':
      return <Cloud className={className} />;
    case 'direct-connect-gateway':
      return <Network className={className} />;
    case 'vpn':
      return <Globe className={className} />;
    case 'peering':
      return <GitBranch className={className} />;
    case 'connect':
      return <Cpu className={className} />;
    default:
      return <Link2 className={className} />;
  }
};

export const TGWAttachmentNode: React.FC<NodeProps<TGWAttachmentNodeData>> = ({ data }) => {
  const style = ATTACHMENT_STYLES[data.type] || ATTACHMENT_STYLES['vpc'];
  
  const stateIcon = () => {
    if (data.state === 'available') {
      return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />;
    }
    if (data.state === 'pending' || data.state === 'modifying') {
      return <AlertCircle className="w-3.5 h-3.5 text-amber-500" />;
    }
    return <XCircle className="w-3.5 h-3.5 text-red-500" />;
  };

  return (
    <div className={`tgw-attachment-node rounded-lg p-3 ${style.bg} ${style.border} border-2 min-w-[160px] shadow-md`}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <AttachmentIcon type={data.type} className={`w-5 h-5 ${style.iconColor}`} />
        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${style.bg} ${style.iconColor}`}>
          {style.label}
        </span>
        {stateIcon()}
      </div>
      
      {/* Resource Info */}
      <div className="space-y-1 text-xs">
        <div className="font-medium text-gray-900 truncate">
          {data.resourceName || data.resourceId}
        </div>
        
        {data.vpcCidr && (
          <div className="font-mono text-gray-600">{data.vpcCidr}</div>
        )}
        
        {data.peeringRegion && (
          <div className="text-gray-500">Region: {data.peeringRegion}</div>
        )}
        
        {data.subnetIds && data.subnetIds.length > 0 && (
          <div className="text-gray-500">{data.subnetIds.length} subnets</div>
        )}
      </div>

      {/* Attachment ID */}
      <div className="mt-2 text-[10px] font-mono text-gray-400 truncate">
        {data.id}
      </div>
      
      <Handle type="source" position={Position.Top} className={`!${style.border.replace('border-', 'bg-')}`} />
      <Handle type="target" position={Position.Bottom} className={`!${style.border.replace('border-', 'bg-')}`} />
    </div>
  );
};

// =============================================================================
// NODE TYPES EXPORT
// =============================================================================

export const tgwNodeTypes = {
  tgwHub: TGWNode,
  tgwRouteTable: TGWRouteTableNode,
  tgwAttachment: TGWAttachmentNode,
};

